import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./AddNewGigPage.scss";
import { ErrorLogger } from "../../utils/Logger";
import Navbar from "../../components/CommonComponents/Navbar/Navbar";
import pageTextConfig from "../../assets/configs/Add New Gig Page/textConfig";
import dashBoardIcon from '../../assets/images/AddNewGigPage/dashboard.svg';
import UserGigAgreementPage from "./FinalVerificationPage/UserGigAgreementPage";
import ListingdetailsPage, { GigFornDataInterface } from "./ListingDetailsPage/ListingdetailsPage";
import PaymentSetupPage, { SellerPaymentFormData } from "./PaymentSetupPage/PaymentSetupPage";
import AddSpace from "../../components/UtilityComponents/AddSpace/AddSpace";
import MultiPageFormProgressBar from "../../components/UtilityComponents/MultiPageFormProgressBar/MultiPageFormProgressBar";
import { AcceptPaymentMethodEnum } from "../../enums/AcceptPaymentMethodEnum";
import SnackBar, { SnackTypes } from "../../components/UtilityComponents/SnackBar/SnackBar";
import { GigAPIHandler } from "../../services/NetworkClient/BackendAPIs/GigAPIHandler";
import GGLoaderView from "../../components/UtilityComponents/GGLoaderView/GGLoaderView";
import { SellerAPIHandler } from "../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import CongratulationsPage from "../../components/CommonComponents/CongratulationsPage/CongratulationsPage";
import congratsPageConfig from "../../assets/configs/Add New Gig Page/CongratulationsPage/textConfig";
import { SellerInterface } from "../../Models/Seller";
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';
import { compressImage, compressMultipleImages } from "../../utils/FileCompressionUtils";
import { getUploadErrorMessage, ResumableUploadTask } from "../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../services/NetworkClient/BackendAPIs/UploadAPIHandler";

interface AddNewFormFormsInterface {
  gigFormData: GigFornDataInterface
  paymentFormData: SellerPaymentFormData
}

const AddNewGigPage = () => {
  const navigate = useNavigate();
  const state = useLocation().state;
  
  const fetchSellerData = async () => {
    try {
      setLoading(true)
      const seller = state?.seller || await SellerAPIHandler.shared.getSellerByAuthId();
      setSeller(seller);
    } catch (err) {
      ErrorLogger.log(err, 'AddNewGigPage.fetchSellerData');
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    document.title = "GenieGig | Add New Gig";
    logAnalyticsEvent(GGAnalyticsEvents.ADD_NEW_GIG_PAGE_LOADED);
    fetchSellerData()
    // eslint-disable-next-line
  }, []);

  const cacheDataKey = "gigFormData"
  const [seller, setSeller] = useState<SellerInterface>();  
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completedPage, setCompletedPage] = useState(1);
  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  // following flag is needed to know if data is already in session and not to fetch from cache if already in session
  const [requireDataFromCache, setRequireDataFromCache] = useState(true);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [allFormsData, setAllFormsData] = useState<AddNewFormFormsInterface>({
    gigFormData: {
      title: "",
      description: "",
      categoryName: undefined,
      categoryId: undefined,
      subcategoryName: undefined,
      subcategoryId: undefined,
      images: [],
      isListed: true,
      priceTable: [
        {
          minPrice: undefined,
          maxPrice: undefined,
          duration: undefined,
          durationUnit: "days",
          description: "",
        }
      ]
    }, 
    paymentFormData: {
      selectedPaymentMode: AcceptPaymentMethodEnum.UPI,
      upiId: "",
      acknowledgementChecked: false,
      bankAccountDetails: {
        accountNumber: "",
        bankIFSC: "",
        holderName: "",
        bankName: "",
      }
    }
  })
  

  const setGigFormData = (formData: GigFornDataInterface) => {
    let newData = {
      ...allFormsData,
      gigFormData: formData
    }
    setRequireDataFromCache(false);
    setAllFormsData(newData)

    // now remove the images content from data to avoid runtime crashes when next time loaded from cache
    let dataWithoutMedia = {
        ...formData,
        images: []
      }
    
    saveDataToCache(dataWithoutMedia)
  }

  const setPaymentFormData = (formData: SellerPaymentFormData) => {
    let newData = {
      ...allFormsData,
      paymentFormData: formData
    }
    setRequireDataFromCache(false);
    setAllFormsData(newData)
  }

  const saveDataToCache = (data: GigFornDataInterface) => {
    let json = JSON.stringify(data);
    localStorage.setItem(cacheDataKey, json);
  }

  const clearDataFromCache = () => {
    localStorage.removeItem(cacheDataKey)
  }

  const fetchCachedData = (): GigFornDataInterface | undefined => {
    if (!requireDataFromCache) return undefined;
    let data = localStorage.getItem(cacheDataKey)
    if (data) {
      let parsedData = JSON.parse(data) as GigFornDataInterface
      return parsedData
    }
    return undefined
  }

   const performFormSubmission = async () => {
    setLoading(true)
    try {
      await addPaymentMethod()
      await performGigCreation()
    } catch (error) {
      ErrorLogger.log(error, 'AddNewGigPage.performFormSubmission');
      showSnackbar(getUploadErrorMessage(error), SnackTypes.error);
    } finally {
      setLoading(false)
    }
   }

   const performGigCreation = async () => {
      let gigData = {
        title: allFormsData.gigFormData.title,
        description: allFormsData.gigFormData.description,
        priceTable: allFormsData.gigFormData.priceTable,
        subcategory: allFormsData.gigFormData.subcategoryId,
        gigImgs: [],
        coverImg: "",
      }

      let imagesToCompress: File[] = []
      
      allFormsData.gigFormData.images.forEach(image => {
        let file = image.file
        if (!file) return 
        imagesToCompress.push(file)
      })
      
      await handleGigPortfolioImageUploads(imagesToCompress, gigData)
      

      await GigAPIHandler.shared.createGig(gigData)
      showSnackbar("Gig was successfully created.", SnackTypes.success);
      clearDataFromCache()
      setShowCongratulations(true)
   }

   const handleGigPortfolioImageUploads = async (imagesToUpload: File[], gigData: any) => {
      // Handle portfolio images
      const compressedImages = await compressMultipleImages(imagesToUpload, 0.150)
      const portfolioAssets = await handleFilesUpload(
        compressedImages,
        UploadPurpose.GIG_PORTFOLIO_IMAGE,
      )
      gigData.gigImgs = portfolioAssets.map((asset: any, position: number) => ({
        assetId: asset.assetId,
        url: asset.url,
        position,
      }))

      // Handle Cover Image
      const compressedCoverImage = await compressImage(imagesToUpload[0], 0.030, 0.3)
      const coverAsset = (await handleFilesUpload(
        [compressedCoverImage],
        UploadPurpose.GIG_COVER_IMAGE,
      ))[0]
      gigData.coverImgAssetId = coverAsset.assetId
      gigData.coverImg = coverAsset.url
   }

   const handleFilesUpload = async (files: File[], purpose: UploadPurpose) => {
    return Promise.all(files.map((file) =>
      new ResumableUploadTask(file, { purpose }).start(),
    ));
  }

  const addPaymentMethod = async () => {
    let selectedPaymentDetails: string
    switch (allFormsData.paymentFormData.selectedPaymentMode) {
      case AcceptPaymentMethodEnum.UPI:
        selectedPaymentDetails = JSON.stringify(allFormsData.paymentFormData.upiId)
        break;
      case AcceptPaymentMethodEnum.BankAccount:
        selectedPaymentDetails = JSON.stringify(allFormsData.paymentFormData.bankAccountDetails)
        break;
      default: 
        selectedPaymentDetails = ""
    }
    let paymentDetails = [
        {
          paymentMethod: allFormsData.paymentFormData.selectedPaymentMode,
          paymentDetails: selectedPaymentDetails
        }
    ]
    await SellerAPIHandler.shared.updateSeller({paymentDetails})
   }

  useEffect(() => {
    setCompletedPage(Math.max(currentPage, completedPage));
    let cachedData = fetchCachedData() 
    if (cachedData) {
      setAllFormsData({...allFormsData, gigFormData: cachedData})
    }
    // eslint-disable-next-line
  }, [completedPage, currentPage]);

  return (
    <Box className="add-new-gig" >
      <Navbar useScrollVersion={true} />
      {
        showCongratulations ? <CongratulationsPage title={congratsPageConfig.title}
                                                   description={congratsPageConfig.description} 
                                                   buttonText={congratsPageConfig.buttonText} 
                                                   buttonIcon={<img src = {dashBoardIcon} alt=""/>} 
                                                   onClickHandler={() => navigate("/freelancer-dashboard")} 
                                                   needTopSpace/> : 
        <>
        <AddSpace vSpace={"10vh"} hSpace={"0"} />
        <AddNewGigTitleBar />
        <MultiPageFormProgressBar
          pageListWithInfo={(seller?.paymentDetails && seller?.paymentDetails.length > 0) ? pageTextConfig.multiPageFormPageList.withoutPaymentPage : pageTextConfig.multiPageFormPageList.withPaymentPage}
          completedTill={completedPage}
          updateCurrentPage={setCurrentPage}
        />
        {  /* switching based on page number for the 3 page form */ }
        { (seller?.paymentDetails && seller?.paymentDetails.length > 0) ?
          {
            1: <ListingdetailsPage setCurrentPage={setCurrentPage} savedFormData={allFormsData.gigFormData} saveGigFormData={setGigFormData} />,
            2: <UserGigAgreementPage setCurrentPage={setCurrentPage} submitForm={performFormSubmission}/>
          }[currentPage] :

          {
            1: <ListingdetailsPage setCurrentPage={setCurrentPage} savedFormData={allFormsData.gigFormData} saveGigFormData={setGigFormData} />,
            2: <PaymentSetupPage setCurrentPage={setCurrentPage} savedFormData={allFormsData.paymentFormData} savePaymentFormData={setPaymentFormData}/>,
            3: <UserGigAgreementPage setCurrentPage={setCurrentPage} submitForm={performFormSubmission}/>
          }[currentPage]
        }
      </>
      }


      
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
      {loading && <GGLoaderView />}
    </Box>
  );
};

export const AddNewGigTitleBar = () => {
  return (
      <Box className = "title-bar" >
           <Typography variant="h6" fontWeight="1000">
              {pageTextConfig.addNewGigPageTitle}
          </Typography>
      </Box>
  )
}

export default AddNewGigPage;
